import React, { useEffect, useState, Component } from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useTenantSlug } from '../../lib/use-tenant-slug';
import { supabase } from '../../lib/supabase';
import { useAdminOrders, useInventoryData, useDailySalesSummary } from '../../lib/admin-hooks';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Skeleton } from '../../components/ui/Skeleton';
import { formatCurrency, formatRelativeTime, getStatusColor, getStatusLabel } from '../../lib/utils';
import { ShoppingBag, Clock, CheckCircle2, AlertTriangle, Play, Pause, Eye, Utensils, Bike, ShoppingBasket, Send } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { OrderDetailDrawer } from '../../components/admin/OrderDetailDrawer';
import { PayTableModal } from '../../components/admin/PayTableModal';
import { Receipt } from 'lucide-react';
import { buildWhatsAppUrl } from '../../lib/whatsapp';
export function DashboardPage() {
  const slug = useTenantSlug();
  const {
    restaurant,
    settings,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const {
    orders,
    loading: ordersLoading,
    updateOrderStatus,
    refetchOrders
  } = useAdminOrders(restaurant?.id);
  const {
    summary,
    loading: salesLoading
  } = useDailySalesSummary(restaurant?.id);
  const {
    alerts,
    loading: invLoading
  } = useInventoryData(restaurant?.id);
  const [kitchenPaused, setKitchenPaused] = useState(false);
  const [togglingKitchen, setTogglingKitchen] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  // ─── Contador de envíos al mensajero (estado optimista local) ──────────
  // Se actualiza al instante cuando se presiona "Enviar a mensajero" para que
  // el dato sea visible de inmediato, sin depender de la latencia o de que la
  // escritura en la base se confirme. Se sincroniza con courier_notify_count
  // de la orden (la fuente compartida) tomando siempre el mayor de ambos.
  const [courierCounts, setCourierCounts] = useState<Record<string, number>>({});
  // ─── Órdenes con al menos un pago registrado ───────────────────────────
  // pos_quick_sale sin payments = intento abandonado, no pedido abierto real.
  // Cargamos el set de order_ids que SÍ tienen un payment para poder excluir
  // del listado de "pedidos abiertos" las ventas rápidas de Estantería que
  // quedaron huérfanas (creadas al abrir el modal de cobro y abandonadas sin
  // pagar). Las ventas rápidas que sí tienen pago NO se excluyen.
  const [paidOrderIds, setPaidOrderIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!restaurant?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const {
          data,
          error
        } = await supabase.from('payments').select('order_id').eq('restaurant_id', restaurant.id);
        if (error) throw error;
        if (cancelled) return;
        setPaidOrderIds(new Set((data || []).map((p: any) => String(p.order_id))));
      } catch (e) {
        console.error('[NOIZA][Dashboard] Error loading paid order ids:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [restaurant?.id, orders]);
  // ─── Cobro modal state ─────────────────────────────────────────────────
  // El botón "Cobrar y Facturar" abre PayTableModal con una "mesa" sintética
  // construida a partir de la orden. PayTableModal ya soporta órdenes sin
  // mesa real (delivery/pickup) usando orderId como fallback en
  // useTableOrderItems. Reutilizamos así TODA la funcionalidad de cobro
  // (split, parcial, factura "ahora / más tarde", auto-print a NOIZA_FACTURA).
  const [cobrarOrder, setCobrarOrder] = useState<any | null>(null);
  useEffect(() => {
    if (settings) {
      console.log('[NOIZA][Dashboard] Settings loaded, kitchen_paused =', settings.kitchen_paused, 'restaurant_id =', restaurant?.id);
      setKitchenPaused(settings.kitchen_paused);
    }
  }, [settings, restaurant?.id]);
  const handleToggleKitchen = async () => {
    if (!restaurant) {
      console.error('[NOIZA][Dashboard] No restaurant loaded — cannot toggle');
      return;
    }
    setTogglingKitchen(true);
    setToggleError(null);
    const newValue = !kitchenPaused;
    console.log('[NOIZA][Dashboard] Toggling kitchen:', {
      restaurantId: restaurant.id,
      slug,
      currentValue: kitchenPaused,
      newValue
    });
    const {
      toggleKitchenPause
    } = await import('../../lib/kitchen-toggle');
    const result = await toggleKitchenPause(restaurant.id, newValue);
    if (result.success) {
      setKitchenPaused(result.newValue);
      setToggleError(null);
    } else {
      setKitchenPaused(result.newValue);
      setToggleError(result.error || 'No se pudo actualizar el estado de cocina');
      console.error('[NOIZA][Dashboard] Toggle FAILED:', result.error);
    }
    setTogglingKitchen(false);
  };
  // Get tenant context for role-aware layout
  const tenantCtx = (() => {
    try {
      const {
        useTenant
      } = require('../../lib/tenant-context');
      return useTenant();
    } catch {
      return null;
    }
  })();
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-3457">
        <Skeleton className="w-16 h-16 rounded-full" data-id="element-3458" />
      </div>;
  }
  const todayOrdersCount = summary?.orders_today || 0;
  const todayRevenue = summary?.total_today || 0;
  const newOrders = orders.filter(o => o.status === 'new');
  const preparingOrders = orders.filter(o => o.status === 'preparing');
  const readyOrders = orders.filter(o => o.status === 'ready');
  // ─── Open orders bucketed by delivery type ─────────────────────────────
  // Used for the new 3-card header (mesa / para llevar / delivery) and
  // the 3 collapsible "Pedidos Recientes" rows. Open = not delivered and
  // not cancelled. Same bucketing logic that Rancho uses below.
  const openOrders = orders.filter(o => o.status !== 'delivered' && o.status !== 'cancelled' &&
  // pos_quick_sale sin payments = intento abandonado, no pedido abierto
  // real. Una venta rápida de Estantería se crea al abrir el modal de
  // cobro; si se abandona sin pagar queda pending/ready con
  // total_pagado=0. La excluimos para que no infle los contadores de
  // "pedidos de mesa". Las ventas rápidas que SÍ tienen pago se conservan.
  !((o as any).source === 'pos_quick_sale' && !paidOrderIds.has(String(o.id))));
  const openMesaOrders = openOrders.filter(o => o.delivery_type === 'dine_in' || o.delivery_type === 'mesa' || (o as any).table_number != null);
  const openPickupOrders = openOrders.filter(o => o.delivery_type === 'pickup');
  const openDeliveryOrders = openOrders.filter(o => o.delivery_type === 'delivery');
  // Heladería Congelados: flujo simplificado (Recibido / Completado)
  const isCongelados = String(restaurant?.id) === '18' || restaurant?.slug === 'heladeria-congelados';
  // Rancho San Francisco (id 28) — vista de cuadros por tipo de orden.
  // Muestra los pedidos ACTIVOS (no entregados ni cancelados) agrupados
  // en tres buckets: Restaurante (mesa), Delivery y Para llevar.
  const isRanchoSanFrancisco = String(restaurant?.id) === '28' || restaurant?.slug === 'rancho-san-francisco';
  const ranchoActiveOrders = orders.filter(o => o.status !== 'delivered' && o.status !== 'cancelled');
  const ranchoMesaOrders = ranchoActiveOrders.filter(o => o.delivery_type === 'dine_in' || o.delivery_type === 'mesa' || (o as any).table_number != null);
  const ranchoDeliveryOrders = ranchoActiveOrders.filter(o => o.delivery_type === 'delivery');
  const ranchoPickupOrders = ranchoActiveOrders.filter(o => o.delivery_type === 'pickup');
  // Derive alert counts from row-level v_inventory_alerts (real view uses uppercase values)
  const criticalAlerts = alerts.filter(a => a.alert_level === 'CRITICAL');
  const lowAlerts = alerts.filter(a => a.alert_level === 'LOW');
  // ─── Build a synthetic TableWithOrder for PayTableModal ───────────────
  // PayTableModal espera una "mesa" pero solo lee:
  //   table.id, table.table_number, table.current_order.* (id, order_number,
  //   created_at, subtotal, total, notes, payment_method).
  // Para órdenes de pickup/delivery no hay mesa real → construimos un
  // objeto mínimo. PayTableModal ya tiene fallback por orderId para
  // useTableOrderItems (línea 60-69 de PayTableModal), así que los items
  // se cargan correctamente vía orderId aunque table.id sea null.
  const buildSyntheticTable = (order: any): any => ({
    id: (order as any).table_id ?? null,
    restaurant_id: String(restaurant.id),
    table_number: (order as any).table_number ?? null,
    table_name: null,
    zone: null,
    capacity: null,
    qr_code_url: null,
    qr_token: null,
    is_active: true,
    created_at: order.created_at,
    updated_at: order.created_at,
    current_order: order,
    current_order_items: [],
    status: 'occupied',
    order_channel: null,
    time_open_minutes: null,
    item_count: 0
  });
  // ─── Enviar pedido a mensajero por WhatsApp + registrar el envío ─────────
  // Abre WhatsApp (de forma síncrona para no chocar con el bloqueo de
  // popups) y luego PERSISTE en la base de datos `courier_notified_at` /
  // `courier_notified_by`, de modo que TODOS los colaboradores vean si el
  // pedido ya fue enviado al mensajero (estado compartido, no local).
  const handleSendCourier = async (order: any) => {
    const url = buildCourierWhatsAppUrl(order, restaurant);
    // 1) Abrir WhatsApp inmediatamente dentro del gesto del usuario.
    const popup = window.open('', '_blank');
    if (popup) {
      popup.location.href = url;
    } else {
      window.open(url, '_blank');
    }
    // 2) Subir el contador LOCAL al instante para que el dato sea visible
    //    inmediatamente (no depende de la base ni del refetch).
    const dbCount = Number(order.courier_notify_count) || 0;
    const localPrev = courierCounts[String(order.id)] || 0;
    const newCount = Math.max(dbCount, localPrev) + 1;
    setCourierCounts(prev => ({
      ...prev,
      [String(order.id)]: newCount
    }));
    // 3) Persistir en la orden (fuente compartida entre colaboradores).
    //    - courier_notified_at / by: SIEMPRE el último envío.
    //    - courier_first_notified_at: solo la PRIMERA vez (inmutable), para
    //      medir el tiempo desde que entró el pedido hasta el 1er despacho.
    //    - courier_notify_count: cuántas veces se envió.
    try {
      const nowIso = new Date().toISOString();
      const update: Record<string, any> = {
        courier_notified_at: nowIso,
        courier_notified_by: tenantCtx?.userId || 'staff',
        courier_notify_count: newCount
      };
      if (!order.courier_first_notified_at) {
        update.courier_first_notified_at = nowIso;
      }
      const {
        error
      } = await supabase.from('orders').update(update).eq('id', order.id);
      if (error) throw error;
      if (refetchOrders) refetchOrders();
    } catch (e) {
      // Si la escritura falla (p. ej. faltan las columnas en la base), el
      // contador LOCAL ya quedó visible para esta sesión. Avisamos en consola.
      console.error('[NOIZA][Dashboard] No se pudo guardar el contador de envíos al mensajero. Verificá que se haya corrido ADD_NATUCAFE_COURIER_NOTIFIED.sql:', e);
    }
  };
  // Cantidad efectiva de envíos = el mayor entre el contador local (sesión)
  // y el guardado en la orden (compartido). Así el dato siempre se ve.
  const courierCountFor = (order: any): number => Math.max(courierCounts[String(order.id)] || 0, Number(order.courier_notify_count) || 0);
  const courierWasSent = (order: any): boolean => courierCountFor(order) > 0 || !!order.courier_notified_at;
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-3459">
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-id="element-3460">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-3461">
          <div data-id="element-3462">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-3463">Dashboard</h1>
            <p className="text-gray-500" data-id="element-3464">Resumen de operaciones de hoy</p>
          </div>
          <div className="flex items-center gap-3" data-id="element-3465">
            <button onClick={handleToggleKitchen} disabled={togglingKitchen} className={`flex items-center gap-2 px-4 py-2 rounded-xl border shadow-sm transition-all active:scale-[0.98] ${togglingKitchen ? 'opacity-50 pointer-events-none' : ''} ${kitchenPaused ? 'bg-red-50 border-red-200 hover:bg-red-100' : 'bg-white border-gray-200 hover:bg-gray-50'}`} data-id="element-3466">
              <span className="text-sm font-medium text-gray-600" data-id="element-3467">Cocina:</span>
              {kitchenPaused ? <Badge variant="danger" className="flex items-center gap-1" data-id="element-3468">
                  <Pause className="w-3 h-3" data-id="element-3469" /> Pausada
                </Badge> : <Badge variant="success" className="flex items-center gap-1" data-id="element-3470">
                  <Play className="w-3 h-3" data-id="element-3471" /> Activa
                </Badge>}
            </button>
          </div>
        </div>

        {/* Toggle Error */}
        {toggleError && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center justify-between" data-id="element-3472">
            <p className="text-red-700 text-sm font-medium" data-id="element-3473">⚠️ {toggleError}</p>
            <button onClick={() => setToggleError(null)} className="text-red-400 hover:text-red-600 text-xs font-bold ml-4" data-id="element-3474">
              ✕
            </button>
          </div>}

        {/* Rancho San Francisco — 3-column bucket view (above stats) */}
        {isRanchoSanFrancisco && <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3475">
            {[{
          key: 'mesa',
          title: 'Restaurante',
          subtitle: 'Mesas activas',
          icon: Utensils,
          accent: 'bg-amber-50 border-amber-200 text-amber-900',
          badge: 'bg-amber-500 text-white',
          items: ranchoMesaOrders
        }, {
          key: 'delivery',
          title: 'Delivery',
          subtitle: 'Pedidos a domicilio',
          icon: Bike,
          accent: 'bg-blue-50 border-blue-200 text-blue-900',
          badge: 'bg-blue-500 text-white',
          items: ranchoDeliveryOrders
        }, {
          key: 'pickup',
          title: 'Para llevar',
          subtitle: 'Recoge en el local',
          icon: ShoppingBasket,
          accent: 'bg-emerald-50 border-emerald-200 text-emerald-900',
          badge: 'bg-emerald-500 text-white',
          items: ranchoPickupOrders
        }].map(bucket => {
          const Icon = bucket.icon;
          return <div key={bucket.key} className={`rounded-2xl border-2 ${bucket.accent} p-4 flex flex-col min-h-[320px]`} data-id="element-3476">
                  <div className="flex items-center justify-between mb-4" data-id="element-3477">
                    <div className="flex items-center gap-3" data-id="element-3478">
                      <div className="w-10 h-10 rounded-xl bg-white/70 flex items-center justify-center shadow-sm" data-id="element-3479">
                        <Icon className="w-5 h-5" data-id="element-3480" />
                      </div>
                      <div data-id="element-3481">
                        <h3 className="text-lg font-black leading-tight" data-id="element-3482">
                          {bucket.title}
                        </h3>
                        <p className="text-xs font-medium opacity-70" data-id="element-3483">
                          {bucket.subtitle}
                        </p>
                      </div>
                    </div>
                    <span className={`min-w-[32px] h-8 px-2 rounded-full text-sm font-black flex items-center justify-center ${bucket.badge}`} data-id="element-3484">
                      {bucket.items.length}
                    </span>
                  </div>

                  {ordersLoading ? <div className="space-y-2" data-id="element-3485">
                      <Skeleton className="h-16 w-full" data-id="element-3486" />
                      <Skeleton className="h-16 w-full" data-id="element-3487" />
                    </div> : bucket.items.length === 0 ? <div className="flex-1 flex items-center justify-center text-sm font-medium opacity-50 italic" data-id="element-3488">
                      Sin pedidos activos
                    </div> : <div className="space-y-2 overflow-y-auto max-h-[480px] pr-1" data-id="element-3489">
                      {bucket.items.map(order => <button key={order.id} type="button" onClick={() => setSelectedOrderId(String(order.id))} className="w-full bg-white rounded-xl p-3 border border-white/50 shadow-sm hover:shadow-md transition-all text-left" data-id="element-3490">
                          <div className="flex justify-between items-start mb-1.5" data-id="element-3491">
                            <span className="font-black text-gray-900 text-sm" data-id="element-3492">
                              #{order.order_number}
                            </span>
                            <span className="text-sm font-black tabular-nums text-gray-900" data-id="element-3493">
                              {formatCurrency(order.total)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-xs" data-id="element-3494">
                            <span className="font-medium text-gray-700 truncate max-w-[60%]" data-id="element-3495">
                              {(order as any).table_number ? `Mesa ${(order as any).table_number}` : order.customer_name || 'Sin nombre'}
                            </span>
                            <span className="text-gray-500 font-medium" data-id="element-3496">
                              {formatRelativeTime(order.created_at)}
                            </span>
                          </div>
                          <div className="mt-2 flex items-center justify-between" data-id="element-3497">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${getStatusColor(order.status)}`} data-id="element-3498">
                              {getStatusLabel(order.status)}
                            </span>
                            <span className="text-[10px] font-bold text-gray-400 flex items-center gap-1" data-id="element-3499">
                              <Eye className="w-3 h-3" data-id="element-3500" />
                              Ver detalle
                            </span>
                          </div>
                        </button>)}
                    </div>}
                </div>;
        })}
          </div>}

        {/* Stats Grid — 3 cards by order type (open orders only) */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-3501">
          <Card data-id="element-3502">
            <CardContent className="p-6" data-id="element-3503">
              <div className="flex items-center justify-between mb-4" data-id="element-3504">
                <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center" data-id="element-3505">
                  <Utensils className="w-5 h-5 text-amber-600" data-id="element-3506" />
                </div>
                <span className="text-2xl font-bold text-gray-900" data-id="element-3507">
                  {openMesaOrders.length}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-500" data-id="element-3508">
                Pedidos de mesa
              </p>
              <p className="text-xs text-gray-400 mt-1" data-id="element-3509">Abiertos ahora</p>
            </CardContent>
          </Card>

          <Card data-id="element-3510">
            <CardContent className="p-6" data-id="element-3511">
              <div className="flex items-center justify-between mb-4" data-id="element-3512">
                <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center" data-id="element-3513">
                  <ShoppingBasket className="w-5 h-5 text-emerald-600" data-id="element-3514" />
                </div>
                <span className="text-2xl font-bold text-gray-900" data-id="element-3515">
                  {openPickupOrders.length}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-500" data-id="element-3516">
                Pedidos para llevar
              </p>
              <p className="text-xs text-gray-400 mt-1" data-id="element-3517">Abiertos ahora</p>
            </CardContent>
          </Card>

          <Card data-id="element-3518">
            <CardContent className="p-6" data-id="element-3519">
              <div className="flex items-center justify-between mb-4" data-id="element-3520">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-3521">
                  <Bike className="w-5 h-5 text-blue-600" data-id="element-3522" />
                </div>
                <span className="text-2xl font-bold text-gray-900" data-id="element-3523">
                  {openDeliveryOrders.length}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-500" data-id="element-3524">
                Pedidos delivery
              </p>
              <p className="text-xs text-gray-400 mt-1" data-id="element-3525">Abiertos ahora</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3526">
          {/* Recent Orders — 3 collapsible dropdowns by type */}
          <Card className="lg:col-span-2" data-id="element-3527">
            <CardHeader data-id="element-3528">
              <CardTitle data-id="element-3529">Pedidos Recientes</CardTitle>
              <p className="text-xs text-gray-500 mt-1" data-id="element-3530">
                Pedidos abiertos agrupados por tipo. Tocá una fila para
                expandir.
              </p>
            </CardHeader>
            <CardContent data-id="element-3531">
              {ordersLoading ? <div className="space-y-4" data-id="element-3532">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" data-id="element-3533" />)}
                </div> : <div className="space-y-3" data-id="element-3534">
                  <OrderTypeDropdown title="Delivery" subtitle="Pedidos a domicilio" icon={Bike} accent="bg-blue-50 border-blue-200 text-blue-900" badge="bg-blue-500 text-white" orders={openDeliveryOrders} isCongelados={isCongelados} restaurant={restaurant} onSelectOrder={id => setSelectedOrderId(id)} onUpdateStatus={updateOrderStatus} onCobrar={order => setCobrarOrder(order)} onSendCourier={handleSendCourier} courierCountFor={courierCountFor} courierWasSent={courierWasSent} data-id="element-3535" />
                  <OrderTypeDropdown title="Mesa" subtitle="Pedidos en el local" icon={Utensils} accent="bg-amber-50 border-amber-200 text-amber-900" badge="bg-amber-500 text-white" orders={openMesaOrders} isCongelados={isCongelados} restaurant={restaurant} onSelectOrder={id => setSelectedOrderId(id)} onUpdateStatus={updateOrderStatus} onCobrar={order => setCobrarOrder(order)} onSendCourier={handleSendCourier} courierCountFor={courierCountFor} courierWasSent={courierWasSent} data-id="element-3536" />
                  <OrderTypeDropdown title="Para llevar" subtitle="Recoge en el local" icon={ShoppingBasket} accent="bg-emerald-50 border-emerald-200 text-emerald-900" badge="bg-emerald-500 text-white" orders={openPickupOrders} isCongelados={isCongelados} restaurant={restaurant} onSelectOrder={id => setSelectedOrderId(id)} onUpdateStatus={updateOrderStatus} onCobrar={order => setCobrarOrder(order)} onSendCourier={handleSendCourier} courierCountFor={courierCountFor} courierWasSent={courierWasSent} data-id="element-3537" />
                  {openOrders.length === 0 && <div className="text-center py-8 text-gray-500 text-sm" data-id="element-3538">
                      No hay pedidos abiertos
                    </div>}
                </div>}
            </CardContent>
          </Card>

          {/* Alerts & Info */}
          <div className="space-y-6" data-id="element-3539">
            <Card data-id="element-3540">
              <CardHeader data-id="element-3541">
                <CardTitle className="flex items-center gap-2" data-id="element-3542">
                  <AlertTriangle className="w-5 h-5 text-amber-500" data-id="element-3543" />
                  Alertas de Inventario
                </CardTitle>
              </CardHeader>
              <CardContent data-id="element-3544">
                {invLoading ? <Skeleton className="h-20 w-full" data-id="element-3545" /> : alerts.length > 0 ? <div className="space-y-4" data-id="element-3546">
                    {criticalAlerts.length > 0 && <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100" data-id="element-3547">
                        <span className="text-sm font-medium text-red-800" data-id="element-3548">
                          Stock Crítico
                        </span>
                        <Badge variant="danger" data-id="element-3549">{criticalAlerts.length}</Badge>
                      </div>}
                    {lowAlerts.length > 0 && <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100" data-id="element-3550">
                        <span className="text-sm font-medium text-amber-800" data-id="element-3551">
                          Stock Bajo
                        </span>
                        <Badge variant="warning" data-id="element-3552">{lowAlerts.length}</Badge>
                      </div>}
                    <div className="space-y-2 mt-2" data-id="element-3553">
                      {alerts.slice(0, 5).map(alert => <div key={alert.inventory_item_id} className="flex justify-between text-sm" data-id="element-3554">
                          <span className="text-gray-700 truncate" data-id="element-3555">
                            {alert.ingredient_name}
                          </span>
                          <span className="text-gray-500 shrink-0 ml-2" data-id="element-3556">
                            {alert.current_stock} / {alert.minimum_stock}{' '}
                            {alert.unit}
                          </span>
                        </div>)}
                    </div>
                  </div> : <p className="text-sm text-gray-500" data-id="element-3557">
                    No hay alertas de inventario.
                  </p>}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <OrderDetailDrawer isOpen={!!selectedOrderId} onClose={() => setSelectedOrderId(null)} orderId={selectedOrderId} data-id="element-3558" />

      {/* ─── Cobrar y Facturar — Modal de cobro reutilizando PayTableModal ── */}
      <PayTableModal isOpen={!!cobrarOrder} onClose={() => setCobrarOrder(null)} table={cobrarOrder ? buildSyntheticTable(cobrarOrder) : null} restaurantId={restaurant?.id} onSuccess={() => {
      // Refrescamos la lista de órdenes cuando se completa un cobro
      // (parcial o total). PayTableModal se cierra solo a través del
      // botón de cerrar; nosotros nos limitamos a refrescar el state.
      if (refetchOrders) refetchOrders();
    }} createdBy={tenantCtx?.userId || null} data-id="element-3559" />
    </AdminLayout>;
}
// ─── Collapsible dropdown for a single order-type bucket ─────────────────
interface OrderTypeDropdownProps {
  title: string;
  subtitle: string;
  icon: ComponentType<any>;
  accent: string;
  badge: string;
  orders: any[];
  isCongelados: boolean;
  restaurant?: any;
  onSelectOrder: (id: string) => void;
  onUpdateStatus: (id: string, status: string) => void;
  onCobrar: (order: any) => void;
  onSendCourier?: (order: any) => void;
  courierCountFor?: (order: any) => number;
  courierWasSent?: (order: any) => boolean;
}
// ─── WhatsApp para mensajero (solo pedidos delivery) ─────────────────────
// Determina si un pedido delivery tiene datos completos para despachar al
// mensajero: nombre + teléfono del cliente y ALGUNA referencia de ubicación
// (dirección de texto, maps_url o lat/lng).
function deliveryHasCompleteData(order: any): boolean {
  const hasContact = !!order?.customer_name && !!order?.customer_phone;
  const hasLocation = !!order?.delivery_address || !!order?.maps_url || order?.delivery_lat != null && order?.delivery_lng != null;
  return hasContact && hasLocation;
}
// Tiempo desde que ENTRÓ el pedido (created_at) hasta el PRIMER envío al
// mensajero (courier_first_notified_at). Base para medir preparación.
// Devuelve un prefijo legible (ej. "Prep 18 min · ") o '' si no aplica.
function courierPrepLabel(order: any): string {
  const start = order?.created_at;
  const first = order?.courier_first_notified_at || order?.courier_notified_at;
  if (!start || !first) return '';
  const ms = new Date(first).getTime() - new Date(start).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `Prep ${mins} min · `;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `Prep ${h}h ${m}m · `;
}
// Construye el mensaje de WhatsApp listo para enviar al mensajero y devuelve
// el wa.me URL (sin número fijo: el usuario elige el contacto del mensajero).
function buildCourierWhatsAppUrl(order: any, restaurant: any): string {
  const lat = order?.delivery_lat;
  const lng = order?.delivery_lng;
  const hasCoords = lat != null && lng != null;
  const googleMaps = order?.maps_url || (hasCoords ? `https://www.google.com/maps?q=${lat},${lng}` : '—');
  const waze = hasCoords ? `https://waze.com/ul?ll=${lat},${lng}&navigate=yes` : '—';
  const lines = ['🛵 *Nuevo pedido para entrega*', '', `*Restaurante:* ${restaurant?.name || '—'}`, `*Dirección restaurante:* ${restaurant?.address_text || '—'}`, `*Cliente:* ${order?.customer_name || '—'}`, `*Teléfono:* ${order?.customer_phone || '—'}`, `*Dirección de entrega:* ${order?.delivery_address || '—'}`, '', '📍 *Ubicación:*', `Google Maps: ${googleMaps}`, `Waze: ${waze}`, '', `*Pedido:* ${order?.order_number || '—'}`];
  // Número PROVISIONAL del mensajero. Cambiar/parametrizar a futuro.
  const COURIER_PHONE = '+50685316690';
  return buildWhatsAppUrl(COURIER_PHONE, lines.join('\n'));
}
function OrderTypeDropdown({
  title,
  subtitle,
  icon: Icon,
  accent,
  badge,
  orders,
  isCongelados,
  restaurant,
  onSelectOrder,
  onUpdateStatus,
  onCobrar,
  onSendCourier,
  courierCountFor,
  courierWasSent
}: OrderTypeDropdownProps) {
  const [open, setOpen] = useState(orders.length > 0);
  // Auto-open if there are orders, but respect manual collapse.
  // We initialize once based on count; the user can toggle freely after.
  return <div className={`rounded-2xl border-2 ${accent} overflow-hidden`} data-id="element-3560">
      <button type="button" onClick={() => setOpen(v => !v)} className="w-full flex items-center justify-between p-4 hover:bg-white/30 transition-colors text-left" data-id="element-3561">
        <div className="flex items-center gap-3" data-id="element-3562">
          <div className="w-10 h-10 rounded-xl bg-white/70 flex items-center justify-center shadow-sm" data-id="element-3563">
            <Icon className="w-5 h-5" data-id="element-3564" />
          </div>
          <div data-id="element-3565">
            <h3 className="text-base font-black leading-tight" data-id="element-3566">{title}</h3>
            <p className="text-xs font-medium opacity-70" data-id="element-3567">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-3568">
          <span className={`min-w-[32px] h-7 px-2 rounded-full text-sm font-black flex items-center justify-center ${badge}`} data-id="element-3569">
            {orders.length}
          </span>
          <ChevronIcon open={open} data-id="element-3570" />
        </div>
      </button>

      {open && <div className="border-t border-white/40 bg-white/40 p-3 space-y-2" data-id="element-3571">
          {orders.length === 0 ? <div className="py-6 text-center text-sm font-medium opacity-60 italic" data-id="element-3572">
              Sin pedidos abiertos
            </div> : orders.map(order => <div key={order.id} className="bg-white rounded-xl p-3 border border-white/60 shadow-sm" data-id="element-3573">
                <div className="flex flex-wrap items-start justify-between gap-2 mb-2" data-id="element-3574">
                  <div className="flex flex-col" data-id="element-3575">
                    <span className="font-black text-gray-900 text-sm" data-id="element-3576">
                      #{order.order_number}
                    </span>
                    <span className="text-xs text-gray-500" data-id="element-3577">
                      {formatRelativeTime(order.created_at)}
                    </span>
                  </div>
                  <div className="text-right" data-id="element-3578">
                    <div className="text-sm font-black tabular-nums text-gray-900" data-id="element-3579">
                      {formatCurrency(order.total)}
                    </div>
                    <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${getStatusColor(order.status)}`} data-id="element-3580">
                      {getStatusLabel(order.status)}
                    </span>
                  </div>
                </div>

                <div className="text-xs text-gray-700 mb-2 truncate" data-id="element-3581">
                  {(order as any).table_number ? `Mesa ${(order as any).table_number}` : order.customer_name || 'Sin nombre'}
                </div>

                <div className="flex items-center gap-2 flex-wrap" data-id="element-3582">
                  <Button size="sm" onClick={() => onSelectOrder(String(order.id))} data-id="element-3583">
                    <Eye className="w-4 h-4 mr-1" data-id="element-3584" />
                    Ver detalle
                  </Button>
                  {isCongelados ? <>
                      {(order.status === 'new' || order.status === 'accepted') && <Button size="sm" variant="secondary" onClick={() => onUpdateStatus(order.id, 'preparing')} data-id="element-3585">
                          ✅ Recibido
                        </Button>}
                      {(order.status === 'preparing' || order.status === 'ready') && <Button size="sm" variant="secondary" onClick={() => onUpdateStatus(order.id, 'delivered')} data-id="element-3586">
                          🎉 Completado
                        </Button>}
                    </> : <>
                      {order.status === 'accepted' && <Button size="sm" variant="secondary" onClick={() => onUpdateStatus(order.id, 'preparing')} data-id="element-3587">
                          Preparar
                        </Button>}
                    </>}
                </div>

                {/* ─── Cobrar y Facturar — abre PayTableModal con el flow completo ─ */}
                <button type="button" onClick={() => onCobrar(order)} className="mt-3 w-full flex items-center gap-3 bg-orange-500 hover:bg-orange-600 active:scale-[0.99] transition-all text-white font-bold rounded-xl p-3 shadow-sm" data-id="element-3588">
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center shrink-0" data-id="element-3589">
                    <Receipt className="w-5 h-5" data-id="element-3590" />
                  </div>
                  <div className="text-left flex-1 min-w-0" data-id="element-3591">
                    <div className="text-sm font-black leading-tight" data-id="element-3592">
                      Cobrar y Facturar
                    </div>
                    <div className="text-[11px] font-medium opacity-90" data-id="element-3593">
                      Recibir el pago y emitir la factura
                    </div>
                  </div>
                </button>

                {/* ─── Enviar pedido a mensajero — solo pedidos delivery ─── */}
                {order.delivery_type === 'delivery' && (deliveryHasCompleteData(order) ? (courierWasSent ? courierWasSent(order) : !!order.courier_notified_at) ? <button type="button" onClick={() => onSendCourier?.(order)} className="mt-2 w-full flex items-center gap-3 bg-emerald-50 hover:bg-emerald-100 active:scale-[0.99] transition-all text-emerald-800 font-bold rounded-xl p-3 border-2 border-emerald-300" data-id="element-3594">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center shrink-0" data-id="element-3595">
                          <CheckCircle2 className="w-5 h-5 text-white" data-id="element-3596" />
                        </div>
                        <div className="text-left flex-1 min-w-0" data-id="element-3597">
                          <div className="text-sm font-black leading-tight" data-id="element-3598">
                            Enviado a mensajero
                            {(courierCountFor ? courierCountFor(order) : Number(order.courier_notify_count) || 0) >= 1 ? ` (×${courierCountFor ? courierCountFor(order) : Number(order.courier_notify_count) || 0})` : ''}
                          </div>
                          <div className="text-[11px] font-medium opacity-90" data-id="element-3599">
                            {courierPrepLabel(order)}
                            {formatRelativeTime(order.courier_notified_at)}
                            {order.courier_notified_by ? ` · ${String(order.courier_notified_by).slice(0, 8)}` : ''}{' '}
                            · Tocá para reenviar
                          </div>
                        </div>
                      </button> : <button type="button" onClick={() => onSendCourier?.(order)} className="mt-2 w-full flex items-center gap-3 bg-blue-600 hover:bg-blue-700 active:scale-[0.99] transition-all text-white font-bold rounded-xl p-3 shadow-sm" data-id="element-3600">
                        <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center shrink-0" data-id="element-3601">
                          <Send className="w-5 h-5" data-id="element-3602" />
                        </div>
                        <div className="text-left flex-1 min-w-0" data-id="element-3603">
                          <div className="text-sm font-black leading-tight" data-id="element-3604">
                            Enviar pedido a mensajero
                          </div>
                          <div className="text-[11px] font-medium opacity-90" data-id="element-3605">
                            Abre WhatsApp con los datos de entrega
                          </div>
                        </div>
                      </button> : <button type="button" disabled className="mt-2 w-full flex items-center gap-3 bg-gray-100 text-gray-400 cursor-not-allowed font-bold rounded-xl p-3" data-id="element-3606">
                      <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center shrink-0" data-id="element-3607">
                        <Send className="w-5 h-5" data-id="element-3608" />
                      </div>
                      <div className="text-left flex-1 min-w-0" data-id="element-3609">
                        <div className="text-sm font-black leading-tight" data-id="element-3610">
                          Faltan datos de entrega
                        </div>
                        <div className="text-[11px] font-medium opacity-90" data-id="element-3611">
                          Sin dirección ni ubicación para el mensajero
                        </div>
                      </div>
                    </button>)}
              </div>)}
        </div>}
    </div>;
}
function ChevronIcon({
  open
}: {
  open: boolean;
}) {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-3612">
      <polyline points="6 9 12 15 18 9" data-id="element-3613" />
    </svg>;
}
// Helper icon
function ChefHatIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-3614">
      <path d="M17 21a1 1 0 0 0 1-1v-5.35c0-.457.316-.844.727-1.041a4 4 0 0 0-2.134-7.589c-.345 0-.682.041-1.005.115-.428-1.833-2.136-3.134-4.088-3.134s-3.66 1.301-4.088 3.134c-.323-.074-.66-.115-1.005-.115a4 4 0 0 0-2.134 7.589c.411.197.727.584.727 1.041V20a1 1 0 0 0 1 1h12z" data-id="element-3615" />
      <path d="M6 17h12" data-id="element-3616" />
    </svg>;
}